import { createRequiredNumberSchema } from "@/pages/workflows/edit/actions/workflow-edit-action-validation.utils.ts";
import {
  WorkflowTriggerTimingEditor,
  WorkflowTriggerTimingFormFields,
} from "@/pages/workflows/edit/components/WorkflowTriggerTimingEditor.tsx";
import { useCustomScheduleDateFields } from "@/pages/workflows/edit/components/use-custom-schedule-date-fields.ts";
import { useWorkflows } from "@/pages/workflows/workflow-hook.tsx";
import { useWorkflowUpdateTriggerScheduleRule } from "@/queries/workflows/workflow.query.ts";
import {
  ApiWorkflowTriggerScheduleDayType,
  ApiWorkflowTriggerScheduleTimezoneSource,
  isCustomScheduleField,
} from "@/types/workflows/workflow.base-enum.types.ts";
import {
  ApiWorkflow,
  ApiWorkflowTriggerScheduleRule,
  ApiWorkflowVersion,
} from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input, ListItem, Select, Stack, Switch, Title } from "@skailer-com/uikit";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export interface WorkflowEditScheduleRuleProps {
  workflow: ApiWorkflow;
  workflowVersion: ApiWorkflowVersion;
  workflowTriggerScheduleRule: ApiWorkflowTriggerScheduleRule;
}

export interface WorkflowTriggerScheduleRuleRef {
  saveData: () => Promise<boolean>;
}

interface WorkflowTriggerScheduleRuleForm extends WorkflowTriggerTimingFormFields {
  direction: "before" | "after";
  days: number;
  day_type: ApiWorkflowTriggerScheduleDayType;
  trigger_hour_local: number;
  timezone_source: ApiWorkflowTriggerScheduleTimezoneSource;
  timezone_name: string | null;
  entity_field_id: number | null;
  is_annual: boolean;
}

export const WorkflowTriggerScheduleRule = forwardRef<
  WorkflowTriggerScheduleRuleRef,
  WorkflowEditScheduleRuleProps
>(({ workflow, workflowVersion, workflowTriggerScheduleRule }, ref) => {
  const { t } = useTranslation();
  const { getWorkflowTriggerScheduleDirectionOptions } = useWorkflows();
  const isCustomField = isCustomScheduleField(workflowTriggerScheduleRule.entity_field);
  const { options: dateFieldOptions, isLoading: isDateFieldsLoading } = useCustomScheduleDateFields(
    workflowTriggerScheduleRule.entity_field,
    isCustomField
  );
  const schemaObject: yup.ObjectSchema<WorkflowTriggerScheduleRuleForm> = useMemo(
    () =>
      yup.object({
        direction: yup.mixed<"before" | "after">().oneOf(["before", "after"]).required(),
        days: createRequiredNumberSchema(
          t("errors:field_required"),
          t("errors:field_number_required")
        ),
        day_type: yup
          .mixed<ApiWorkflowTriggerScheduleDayType>()
          .oneOf(Object.values(ApiWorkflowTriggerScheduleDayType))
          .required(t("errors:field_required")),
        trigger_hour_local: yup
          .number()
          .integer(t("errors:field_number_required"))
          .min(0)
          .max(23)
          .required(t("errors:field_required")),
        timezone_source: yup
          .mixed<ApiWorkflowTriggerScheduleTimezoneSource>()
          .oneOf(Object.values(ApiWorkflowTriggerScheduleTimezoneSource))
          .required(t("errors:field_required")),
        timezone_name: yup
          .string()
          .nullable()
          .when("timezone_source", {
            is: ApiWorkflowTriggerScheduleTimezoneSource.custom,
            then: (schema) => schema.required(t("errors:field_required")),
            otherwise: (schema) => schema.nullable(),
          })
          .defined(),
        entity_field_id: isCustomField
          ? yup.number().required(t("errors:field_required"))
          : yup.number().nullable().defined(),
        is_annual: yup.boolean().required(),
      }),
    [t, isCustomField]
  );

  const form = useForm<WorkflowTriggerScheduleRuleForm>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    defaultValues: {
      direction: workflowTriggerScheduleRule.offset_days <= 0 ? "before" : "after",
      days: Math.abs(workflowTriggerScheduleRule.offset_days),
      day_type: workflowTriggerScheduleRule.day_type ?? ApiWorkflowTriggerScheduleDayType.calendar,
      trigger_hour_local: workflowTriggerScheduleRule.trigger_hour_local ?? 9,
      timezone_source:
        workflowTriggerScheduleRule.timezone_source ??
        ApiWorkflowTriggerScheduleTimezoneSource.organization,
      timezone_name: workflowTriggerScheduleRule.timezone_name ?? null,
      entity_field_id: workflowTriggerScheduleRule.entity_field_id,
      is_annual: workflowTriggerScheduleRule.is_annual,
    },
  });
  const {
    control,
    getValues,
    register,
    trigger,
    formState: { errors, isDirty },
  } = form;

  const updateTriggerScheduleRule = useWorkflowUpdateTriggerScheduleRule(control);

  const handleSave = async (): Promise<boolean> => {
    if (!workflowVersion.trigger_schedule_rule) return false;
    if (!(await trigger())) return false;
    if (!isDirty) return true;

    const values = getValues();

    try {
      const result1 = await updateTriggerScheduleRule.mutateAsync({
        workflowId: workflow.id,
        workflowVersionId: workflow.current_version_id,
        payload: {
          entity_type: workflowVersion.trigger_schedule_rule.entity_type,
          entity_field: workflowVersion.trigger_schedule_rule.entity_field,
          entity_field_id: isCustomField ? values.entity_field_id : null,
          is_annual: isCustomField && values.is_annual,
          day_type: values.day_type,
          offset_days: values.direction === "before" ? -values.days : values.days,
          offset_hours: 0,
          trigger_hour_local: values.trigger_hour_local,
          timezone_source: values.timezone_source,
          timezone_name:
            values.timezone_source === ApiWorkflowTriggerScheduleTimezoneSource.custom
              ? values.timezone_name
              : null,
        },
      });
      if (!result1.success) return false;
    } catch {
      return false;
    }

    return true;
  };

  const entityFieldId = useWatch({ control, name: "entity_field_id" });
  const isHistoricalTableField =
    dateFieldOptions.find((option) => option.id === entityFieldId)?.isHistorical ?? false;

  useImperativeHandle(ref, () => ({
    saveData: handleSave,
  }));

  return (
    <Stack>
      {isCustomField && (
        <Stack gap={"md"}>
          <Controller
            control={control}
            name={"entity_field_id"}
            render={({ field: { value, onChange } }) => (
              <Select
                value={value}
                label={t("workflow:trigger_schedule_date_field")}
                emptyTitle={t("workflow:trigger_schedule_date_field_placeholder")}
                renderSelectedTitle={([option]) =>
                  option?.group ? `${option.group} → ${option.title}` : option?.title
                }
                options={dateFieldOptions}
                isLoading={isDateFieldsLoading}
                searchable={true}
                required={true}
                error={errors.entity_field_id?.message}
                onChange={onChange}
              />
            )}
          />
          {isHistoricalTableField && (
            <ListItem
              emoji={":information_source:"}
              caption={t("workflow:trigger_schedule_date_field_historical_hint")}
              behavior={"card"}
            />
          )}
          <Controller
            control={control}
            name={"is_annual"}
            render={({ field: { value, onChange } }) => (
              <Switch
                size={"sm"}
                checked={value}
                onChange={onChange}
                title={t("workflow:trigger_schedule_is_annual_title")}
                description={t("workflow:trigger_schedule_is_annual_description")}
              />
            )}
          />
        </Stack>
      )}
      <Title header={t("common:time")} size={"xs"} paddingTop={true} />
      <Controller
        control={control}
        name={"direction"}
        render={({ field: { value, onChange } }) => (
          <Select
            value={value}
            emptyTitle={t("common:type")}
            options={getWorkflowTriggerScheduleDirectionOptions()}
            onChange={onChange}
          />
        )}
      />
      <Input
        type={"number"}
        label={t("common:days")}
        error={errors.days?.message}
        {...register("days")}
      />
      <WorkflowTriggerTimingEditor form={form} />
    </Stack>
  );
});
